import { useState } from "react";
import type { JSX } from "react";
import { useIdentity } from "./hooks/useIdentity";
import { useGroups } from "./hooks/useGroups";
import type { IdentityProfile } from "./types";
import { Icon, PageSectionHeader } from "../../components/ui";

function IdentityEditor({
  profile,
  groupOptions,
  onBack,
  onSave,
  onDelete,
}: {
  readonly profile: IdentityProfile | null;
  readonly groupOptions: readonly { id: string; name: string }[];
  readonly onBack: () => void;
  readonly onSave: (data: {
    name: string;
    iconColor: string;
    content: string;
    is_default: boolean;
    group_ids: string[];
  }) => Promise<void>;
  readonly onDelete?: (id: string) => Promise<void>;
}): JSX.Element {
  const [name, setName] = useState(profile?.name ?? "");
  const [iconColor, setIconColor] = useState(profile?.iconColor ?? "#FF8400");
  const [content, setContent] = useState(profile?.content ?? "");
  const [isDefault, setIsDefault] = useState(profile?.is_default ?? false);
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(
    new Set(profile?.group_ids ?? []),
  );
  const [saving, setSaving] = useState(false);

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const toggleGroup = (id: string) => {
    setSelectedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        name,
        iconColor: iconColor,
        content,
        is_default: isDefault,
        group_ids: Array.from(selectedGroups),
      });
      onBack();
    } finally {
      setSaving(false);
    }
  };

  const COLORS = ["#FF8400", "#8B5CF6", "#3B82F6", "#22C55E", "#F59E0B", "#EF4444", "#EC4899", "#06B6D4"];

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-2">
        <div className="flex items-center gap-3">
          <button type="button" onClick={onBack} className="cursor-pointer shrink-0">
            <Icon name="chevron-left" size={22} className="text-accent" />
          </button>
          <h2 className="text-lg font-semibold leading-tight text-content">
            {profile ? "Edit Profile" : "New Profile"}
          </h2>
        </div>

        <div className="h-3" />

        <div className="space-y-4">
          <div>
            <label className="text-[11px] font-bold tracking-wider text-accent uppercase">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); }}
              placeholder="e.g. Professional Bio, Family Bio..."
              className="mt-1.5 w-full px-4 py-3 rounded-xl bg-surface-secondary border border-edge text-sm text-content placeholder:text-content-muted focus:border-accent focus:outline-none"
            />
          </div>

          <div>
            <label className="text-[11px] font-bold tracking-wider text-accent uppercase">Color</label>
            <div className="flex gap-2 mt-1.5">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => { setIconColor(c); }}
                  className={`w-8 h-8 rounded-full cursor-pointer transition-all ${
                    iconColor === c ? "ring-2 ring-accent ring-offset-2 ring-offset-surface" : ""
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>

          <div>
            <label className="text-[11px] font-bold tracking-wider text-accent uppercase">Bio Content</label>
            <textarea
              value={content}
              onChange={(e) => { setContent(e.target.value); }}
              rows={8}
              placeholder="Write a bio in markdown..."
              className="mt-1.5 w-full px-4 py-3 rounded-xl bg-surface-secondary border border-edge text-sm text-content placeholder:text-content-muted focus:border-accent focus:outline-none resize-none font-mono"
            />
          </div>

          <div>
            <label className="text-[11px] font-bold tracking-wider text-accent uppercase">Assign to Groups</label>
            <div className="mt-1.5 rounded-xl bg-surface-secondary border border-edge overflow-hidden">
              {groupOptions.map((g) => {
                const checked = selectedGroups.has(g.id);
                return (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => { toggleGroup(g.id); }}
                    className="flex items-center justify-between w-full px-4 py-3 border-b border-edge last:border-b-0 cursor-pointer"
                  >
                    <span className={`text-sm font-medium ${checked ? "text-content" : "text-content-secondary"}`}>
                      {g.name}
                    </span>
                    {checked && <Icon name="check" size={18} className="text-accent" />}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label className="text-[11px] font-bold tracking-wider text-accent uppercase">Default Profile</label>
            <button
              type="button"
              onClick={() => { setIsDefault(!isDefault); }}
              className={`w-10 h-6 rounded-full transition-colors cursor-pointer ${
                isDefault ? "bg-accent" : "bg-[#333]"
              }`}
            >
              <div
                className={`w-4 h-4 rounded-full bg-white transition-transform mx-1 ${
                  isDefault ? "translate-x-4" : ""
                }`}
              />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="px-5 py-2.5 rounded-lg bg-accent text-white text-sm font-semibold disabled:opacity-50 cursor-pointer"
          >
            {saving ? "Saving..." : profile ? "Save Changes" : "Create Profile"}
          </button>
          {profile && onDelete && (
            <button
              type="button"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={async () => {
                await onDelete(profile.id);
                onBack();
              }}
              className="px-5 py-2.5 rounded-lg text-red-400 text-sm font-medium hover:bg-surface-secondary cursor-pointer"
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function IdentityPanel(): JSX.Element {
  const { profiles, createProfile, updateProfile, deleteProfile } = useIdentity();
  const { groups } = useGroups();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const editing = editingId ? profiles.find((p) => p.id === editingId) : null;

  if (isCreating) {
    return (
      <IdentityEditor
        profile={null}
        groupOptions={groups}
        onBack={() => { setIsCreating(false); }}
        onSave={async (data) => {
          await createProfile(data);
        }}
      />
    );
  }

  if (editing) {
    return (
      <IdentityEditor
        profile={editing}
        groupOptions={groups}
        onBack={() => { setEditingId(null); }}
        onSave={async (data) => {
          await updateProfile(editing.id, data);
        }}
        onDelete={deleteProfile}
      />
    );
  }

  return (
    <div className="px-6 py-5 space-y-2">
      <PageSectionHeader
        title="Identity Profiles"
        description="Create markdown bio files that tell your agent who you are. Assign different bios to different contact groups so the agent adapts its context to the audience."
        actions={(
          <button
            type="button"
            onClick={() => { setIsCreating(true); }}
            className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer hover:bg-surface-hover"
          >
            <Icon name="plus" size={18} className="text-content-secondary" />
          </button>
        )}
      />

      <div className="h-5" />

      {profiles.length === 0 ? (
        <div className="py-10 text-center text-content-muted text-sm">
          No identity profiles yet. Create one to help the agent understand your context.
        </div>
      ) : (
        <div className="rounded-xl bg-surface-tertiary border border-edge overflow-hidden divide-y divide-edge">
          {profiles.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => { setEditingId(p.id); }}
              className="w-full flex items-center justify-between px-4 py-3.5 text-left cursor-pointer hover:bg-surface-hover transition-colors"
            >
              <span className="text-sm font-medium text-content">{p.name}</span>
              <div className="flex items-center gap-2">
                {p.is_default && (
                  <span className="text-xs text-content-tertiary">Default</span>
                )}
                <Icon name="chevron-right" size={16} className="text-content-muted" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
